// External libraries
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import dayjs from 'dayjs'

// MUI components
import { Box, Checkbox, FormControlLabel, Typography } from '@mui/material'
import type { SelectChangeEvent } from '@mui/material'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'

// Redux
import { useDispatch, useSelector } from 'react-redux'
import { AppDispatch } from '../../../../store/store'
import { TimesheetStoreState } from '../../../../store/timesheetStore'

// Store slices & actions
import { handleDetails } from '../../../../store/slices/timesheet/timesheetPageSlice'
import { KmsVehicle } from '../../../../store/slices/timesheet/timesheetApprovalDetails/timesheetApprovalDetailsSlice'

// API hooks
import { useLazyCalculateAvailabilityQuery } from '../../../../store/api/timesheet/availabilityApi'

// Utils
import { filterWorkTimes } from '../../../../utils/timesheet/workTimes'
import { mapServicesForApproval, mapServicesForEdit } from '../../../../utils/timesheet/services'

// Modal
import { useModal } from '../../../../store/ModalProvider'
import { AvailabilityModalData, modalHandlersType } from '../../../../types/modal'

// Components
import CustomSelect from '../../../v2/general/CustomSelect/CustomSelect'
import CustomButton from '../../../v2/general/CustomButton/CustomButton'
import CustomTextField from '../../../general/CustomTextField/CustomTextField'
import CustomAlertDialog from '../../../general/CustomAlertDialog/CustomAlertDialog'

// Types and Interfaces
type WorkTypeOption = { id: number; value: string }

interface WorkRegimeAndAgentProps {
	workTypes?: WorkTypeOption[]
	isTimeSheetApproved: boolean
	workType: string
	isModal?: boolean
	handleWork: (event: SelectChangeEvent<string | string[]>) => void
}

type ServiceDateRange = { startDateTime?: string | null; endDateTime?: string | null }

// Shared by "Ver Disponibilidade" and "Guardar Alterações" so both stay in sync instead of duplicating the check.
function hasInvalidServiceDates(services: ServiceDateRange[]): boolean {
	return services.some(service => {
		if (service.startDateTime && service.endDateTime) {
			return dayjs(service.endDateTime).isBefore(dayjs(service.startDateTime))
		}
		return false
	})
}

/**
 * WorkRegimeAndAgent component
 * @description This component displays the work regime and agent information for a timesheet. It allows users to select a work type from a dropdown, view their unique agent time, and check their availability based on the services and work times associated with the timesheet. The component also provides an option to automatically recalculate availability when saving the timesheet. It can be rendered in a modal or as part of the main timesheet details page, with styling adjustments accordingly.
 * @param {WorkRegimeAndAgentProps} props - The props for the component, including work types, timesheet approval status, selected work type, modal state, and a handler for work type changes.
 * @returns {JSX.Element} The rendered WorkRegimeAndAgent component.
 */ function WorkRegimeAndAgent({
	workTypes,
	isTimeSheetApproved,
	workType,
	isModal = false,
	handleWork,
}: Readonly<WorkRegimeAndAgentProps>): React.JSX.Element {
	const TimesheetPage = useSelector((state: TimesheetStoreState) => state.timesheetPage)
	const approvalAcceptState = useSelector((state: TimesheetStoreState) => state.timesheetApprovalDetailsAccept)
	const dispatch = useDispatch<AppDispatch>()
	const { openModal, closeModal } = useModal()
	const [isInvalidDatesDialogOpen, setIsInvalidDatesDialogOpen] = useState(false)
	const [saveAvailabilityError, setSaveAvailabilityError] = useState(false)

	// Two separate query instances on purpose: sharing one would feed a save's data/isFetching/isError back into
	// openAvailabilityModal's dependencies and could re-trigger the auto-open effect while the modal is closing.
	const [
		triggerCalculateAvailability,
		{ data: availability, isFetching: isFetchingAvailability, isError: isErrorAvailability },
	] = useLazyCalculateAvailabilityQuery()
	const [triggerSaveAvailability] = useLazyCalculateAvailabilityQuery()

	// Set by handleCalculateAvailability on click, consumed by openAvailabilityModal. Intent-based rather than
	// reference-based: comparing `availability`'s identity across clicks doesn't work because RTK Query returns
	// the same cached object for a repeated request with identical args, which would block a legitimate reopen.
	// It also stops unrelated re-renders (e.g. the main-page "Guardar" save reassigning services/workTimes) from
	// reopening the modal, since those never set the flag.
	const shouldOpenAvailabilityModalRef = useRef(false)

	const onDrivingBreakChange = useCallback(
		(value: number) => {
			dispatch(
				handleDetails({
					dispDrivingBreak: value,
				}),
			)
		},
		[dispatch],
	)

	/**
	 * This memoized value computes the work times and services to be used for availability calculations based on whether the timesheet is approved or not. It filters the work times and maps the services accordingly, ensuring that the correct data is used for availability calculations in both approved and non-approved states. The dependencies for this memo include the timesheet approval status, work times, and services from both the approval state and the main timesheet details to ensure it recomputes whenever any of these values change.
	 * @see {@link filterWorkTimes} for the utility function that filters work times based on specific criteria.
	 * @see {@link mapServicesForApproval} and {@link mapServicesForEdit} for the utility functions that map services for approval and editing states, respectively.
	 * @see {@link useSelector} for accessing the work times and services from both the approval state and the main timesheet details in the Redux store to compute the correct data for availability calculations.
	 */
	const { workTimes, services } = useMemo(() => {
		const sourceWorkTimes = isTimeSheetApproved ? approvalAcceptState.workTimes : TimesheetPage.workTimes
		const computedWorkTimes = filterWorkTimes(sourceWorkTimes)
		const computedServices = isTimeSheetApproved
			? mapServicesForEdit(approvalAcceptState.services)
			: mapServicesForApproval(TimesheetPage.services)
		return { workTimes: computedWorkTimes, services: computedServices }
	}, [
		isTimeSheetApproved,
		approvalAcceptState.workTimes,
		approvalAcceptState.services,
		TimesheetPage.workTimes,
		TimesheetPage.services,
	])

	// Tags the modal payload with `isSaving` explicitly (instead of reading the save query's own state) so
	// "Guardar Alterações" can show a loading state without depending on `triggerSaveAvailability`.
	const buildAvailabilityModalData = useCallback(
		(isSaving: boolean): AvailabilityModalData | null => {
			if (!availability) return null
			return {
				workedSchedule: availability.workedSchedule,
				totalsByPeriod: availability.totalsByPeriod,
				totalAvailability: availability.totalAvailability,
				totalAvailabilityFormatted: availability.totalAvailabilityFormatted,
				overtime: availability.overtime,
				drivingBreak: availability.drivingBreak,
				isSavingAvailability: isSaving,
			}
		},
		[availability],
	)

	// Only commits the selection to Redux (onDrivingBreakChange) after the recalculation succeeds — on failure,
	// Redux is left untouched and the error dialog shows instead. The loading-state openModal call here is direct
	// rather than a dependency of openAvailabilityModal, to avoid re-triggering the auto-open effect while closing.
	const onSaveChanges = useCallback(
		async (value: number) => {
			if (hasInvalidServiceDates(services)) {
				setIsInvalidDatesDialogOpen(true)
				return
			}

			setSaveAvailabilityError(false)

			const savingModalData = buildAvailabilityModalData(true)
			if (savingModalData) {
				openModal('Disponibilidade e Pausas', savingModalData, [onSaveChanges, onDrivingBreakChange] as modalHandlersType)
			}

			try {
				await triggerSaveAvailability({
					TimesheetId: TimesheetPage.timeSheetId,
					ServiceExecutionAvailabilityRequests: services.map(service => ({
						serviceId: service.serviceId,
						pax: service.pax,
						isClean: service.isClean,
						startDateTime: service.startDateTime,
						endDateTime: service.endDateTime,
						isAU: service?.isAU ?? false,
						kmsEmpty: (service.kmsVehicles ?? [])
							.filter((kv: KmsVehicle) => kv.inEmpty)
							.reduce((acc: number, kv: KmsVehicle) => acc + (kv.kmsEnd ?? 0) - kv.kmsIni, 0),
						kmsFull: (service.kmsVehicles ?? [])
							.filter((kv: KmsVehicle) => !kv.inEmpty)
							.reduce((acc: number, kv: KmsVehicle) => acc + (kv.kmsEnd ?? 0) - kv.kmsIni, 0),
						service: null,
					})),
					TimeSheetWorkTimeAvailabilityRequests: workTimes,
					PauseTypeId: value ?? undefined,
				}).unwrap()

				onDrivingBreakChange(value)
			} catch (e) {
				setSaveAvailabilityError(true)
			} finally {
				closeModal()
			}
		},
		// `openModal`/`closeModal` are intentionally excluded: ModalProvider recreates them on every context
		// render (they aren't memoized there), so tracking them here would recreate this callback — and, in
		// turn, `openAvailabilityModal` below — every time the modal's own state changes, which is exactly the
		// kind of render loop this fix needs to avoid. They're stable enough in practice (their behavior never
		// changes between renders), matching how this file already treats them elsewhere.
		[services, workTimes, onDrivingBreakChange, buildAvailabilityModalData, triggerSaveAvailability, TimesheetPage.timeSheetId],
	)

	// Only opens when shouldOpenAvailabilityModalRef is set (see its declaration above) — that's what stops an
	// unrelated services/workTimes identity change (e.g. the main-page "Guardar" save) from reopening the modal
	// with stale data. onSaveChanges/onDrivingBreakChange are in the deps so the modal always gets fresh handlers.
	const openAvailabilityModal = useCallback(() => {
		if (isFetchingAvailability) return

		if (isErrorAvailability) {
			shouldOpenAvailabilityModalRef.current = false
			return
		}

		if (shouldOpenAvailabilityModalRef.current && availability) {
			shouldOpenAvailabilityModalRef.current = false

			if (availability.singleAgent?.singleAgentTime) {
				dispatch(
					handleDetails({
						uniqueAgent: dayjs(`1900-01-01T${availability.singleAgent.singleAgentTime}`).format('YYYY-MM-DDTHH:mm:ss'),
					}),
				)
			}

			const availabilityModalData = buildAvailabilityModalData(false)
			if (availabilityModalData) {
				openModal('Disponibilidade e Pausas', availabilityModalData, [
					onSaveChanges,
					onDrivingBreakChange,
				] as modalHandlersType)
			}
		}
		// `openModal`/`dispatch` are intentionally excluded for the same reason documented in `onSaveChanges`'s
		// dependency array above: `openModal` isn't memoized by ModalProvider, and `dispatch` never changes
		// identity, so neither needs to be tracked to stay correct, and tracking `openModal` would risk a loop.
	}, [availability, isFetchingAvailability, isErrorAvailability, onSaveChanges, onDrivingBreakChange, buildAvailabilityModalData])

	const handleCalculateAvailability = useCallback(() => {
		if (hasInvalidServiceDates(services)) {
			setIsInvalidDatesDialogOpen(true)
			return
		}

		shouldOpenAvailabilityModalRef.current = true

		triggerCalculateAvailability({
			TimesheetId: TimesheetPage.timeSheetId,
			ServiceExecutionAvailabilityRequests: services.map(service => ({
				serviceId: service.serviceId,
				pax: service.pax,
				isClean: service.isClean,
				startDateTime: service.startDateTime,
				endDateTime: service.endDateTime,
				isAU: service?.isAU ?? false,
				kmsEmpty: (service.kmsVehicles ?? [])
					.filter((kv: KmsVehicle) => kv.inEmpty)
					.reduce((acc: number, kv: KmsVehicle) => acc + (kv.kmsEnd ?? 0) - kv.kmsIni, 0),
				kmsFull: (service.kmsVehicles ?? [])
					.filter((kv: KmsVehicle) => !kv.inEmpty)
					.reduce((acc: number, kv: KmsVehicle) => acc + (kv.kmsEnd ?? 0) - kv.kmsIni, 0),
				service: null,
			})),
			TimeSheetWorkTimeAvailabilityRequests: workTimes,
			PauseTypeId: TimesheetPage.dispDrivingBreak ?? undefined,
		})
	}, [triggerCalculateAvailability, TimesheetPage.timeSheetId, TimesheetPage.dispDrivingBreak, services, workTimes])

	/**
	 * This handler updates the recalculation on save preference in the timesheet details when the user toggles the corresponding checkbox. It dispatches an action to update the timesheet details in the Redux store with the new recalculation preference, allowing users to choose whether they want their availability to be automatically recalculated when they save their timesheet. The dependencies for this handler include the dispatch function and the current timesheet details to ensure it has access to the latest state when updating the recalculation preference.
	 * @param {React.ChangeEvent<HTMLInputElement>} event - The change event from the checkbox input that indicates whether the user wants to enable or disable automatic recalculation on save.
	 * @see {@link handleDetails} for the Redux action that updates the timesheet details in the store with the new recalculation preference.
	 * @see {@link onSaveChanges} for the function that handles saving changes and closing the modal, which is relevant to the recalculation preference as it determines whether availability should be recalculated when saving.
	 * @see {@link useSelector} for accessing the current timesheet details from the Redux store to ensure the handler has the latest state when updating the recalculation preference.
	 */
	const handleRecalculateOnSave = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>) => {
			dispatch(
				handleDetails({
					dispRecalculateOnSave: event.target.checked,
				}),
			)
		},
		[TimesheetPage, dispatch],
	)

	/**
	 * This memoized value computes the unique agent field to be displayed in the component based on the unique agent time from the timesheet details. It formats the unique agent time for display and returns a disabled text field with the formatted time. The dependencies for this memo include the unique agent time from the timesheet details to ensure it recomputes whenever this value changes.
	 * @see {@link useSelector} for accessing the unique agent time from the timesheet details in the Redux store to compute the value for the unique agent field.
	 */
	const handleUniqueAgentChange = useCallback(
		(newValue: dayjs.Dayjs | null) => {
			dispatch(
				handleDetails({
					uniqueAgent: newValue?.isValid() ? newValue.format('YYYY-MM-DDTHH:mm:ss') : null,
				}),
			)
		},
		[TimesheetPage, dispatch],
	)

	/**
	 * This memoized value computes the contract code field to be displayed in the component based on the selected work type and the available work types. It finds the corresponding work type object for the selected work type and returns a disabled text field with the contract code. The dependencies for this memo include the selected work type and the available work types to ensure it recomputes whenever either of these values changes.
	 * @see {@link useSelector} for accessing the selected work type and available work types from the Redux store to compute the value for the contract code field.
	 */
	const uniqueAgentField = useMemo(() => {
		return (
			<LocalizationProvider dateAdapter={AdapterDayjs}>
				<CustomTextField
					disabled
					type='time'
					label='Agente Único'
					size='small'
					value={
						TimesheetPage.uniqueAgent
							? dayjs(TimesheetPage.uniqueAgent).format('HH:mm')
							: dayjs('1900-01-01T00:00:00Z').format('HH:mm')
					}
				/>
			</LocalizationProvider>
		)
	}, [TimesheetPage.uniqueAgent, handleUniqueAgentChange])

	/**
	 * This memoized value computes the contract code field to be displayed in the component based on the selected work type and the available work types. It finds the corresponding work type object for the selected work type and returns a disabled text field with the contract code. The dependencies for this memo include the selected work type and the available work types to ensure it recomputes whenever either of these values changes.
	 * @see {@link useSelector} for accessing the selected work type and available work types from the Redux store to compute the value for the contract code field.
	 */
	const contractCode = useMemo(() => {
		return (
			<LocalizationProvider dateAdapter={AdapterDayjs}>
				<CustomTextField
					disabled
					type='number'
					label='Código Contrato'
					size='small'
					value={workTypes?.find(wt => String(wt.id) === workType)?.id ?? 0}
				/>
			</LocalizationProvider>
		)
	}, [])

	/**
	 * This effect runs whenever the component mounts and opens the availability modal if there is availability data available and it's not currently fetching or in an error state. It also defines handlers for saving changes and updating the driving break value, which are passed to the modal when opened. The dependencies for this effect include the availability data, fetching and error states, and the handlers to ensure it runs whenever any of these values change.
	 * @see {@link useLazyCalculateAvailabilityQuery} for the API query that fetches availability data, which is used to determine when to open the modal and what data to display in it.
	 * @see {@link openModal} for the function that opens the modal with the availability data and handlers.
	 */
	useEffect(() => {
		openAvailabilityModal()
	}, [openAvailabilityModal])

	return (
		<>
			<Box
				sx={{
					display: 'flex',
					flexDirection: 'column',
					paddingTop: isModal ? 0 : '1.5rem',
				}}
			>
				<Typography
					variant='h5'
					sx={{
						fontFamily: 'var(--font-family)',
						fontWeight: 600,
						fontSize: isModal ? '1.5rem' : '1rem',
						lineHeight: '175%',
						letterSpacing: '0.15px',
						color: 'var(--primary-dark)',
					}}
				>
					Regime de Trabalho e {isModal ? 'Disponibilidade' : 'Agente Único'}
				</Typography>
				<Box
					sx={{
						display: 'flex',
						flexWrap: 'wrap',
						alignItems: 'baseline',
						justifyContent: 'space-between',
						paddingTop: '1.5rem',
						gap: '2rem',
					}}
				>
					<Box sx={{ display: 'flex', alignItems: 'center', gap: '1rem', justifyContent: 'space-between' }}>
						{/* TODO: replace with actual component UniqueAgent.tsx */}
						<CustomSelect
							defaultValue={workType}
							options={workTypes?.map(type => ({
								value: String(type.id),
								label: type.value,
							}))}
							tabIndex={-1}
							value={workType}
							label='Regime de Trabalho'
							onChange={e => handleWork(e as SelectChangeEvent<string | string[]>)}
							size='small'
							sx={{
								width: '300px',
							}}
						/>
						{!isModal && uniqueAgentField}
						{isModal && contractCode}
					</Box>

					{!isModal && (
						<Box sx={{ display: 'flex', flexDirection: 'column' }}>
							<Box sx={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
								<Typography
									variant='subtitle2'
									sx={{
										fontFamily: 'var(--font-family)',
										fontWeight: 600,
										fontSize: '0.875rem',
										lineHeight: '157%',
										color: 'var(--primary-dark)',
									}}
								>
									Disponibilidade
								</Typography>
								<CustomButton
									color='secondary'
									size='small'
									tabIndex={-1}
									onClick={handleCalculateAvailability}
									isLoading={isFetchingAvailability}
								>
									Ver Disponibilidade
								</CustomButton>
							</Box>
							<FormControlLabel
								control={
									<Checkbox
										size='small'
										defaultChecked={TimesheetPage.dispRecalculateOnSave ?? true}
										onChange={handleRecalculateOnSave}
										tabIndex={-1}
										checked={TimesheetPage.dispRecalculateOnSave ?? true}
									/>
								}
								label='Recalcular automaticamente ao gravar a Folha'
								sx={{
									'& .MuiFormControlLabel-label': {
										fontFamily: 'var(--font-family)',
										fontSize: '0.8125rem',
										fontWeight: 500,
										lineHeight: '130%',
										color: 'var(--primary-dark)',
									},
								}}
							/>
						</Box>
					)}
				</Box>
			</Box>
			<CustomAlertDialog
				open={isInvalidDatesDialogOpen}
				title='Datas de serviço inválidas'
				description='As datas de início e fim dos serviços não estão válidas. Corrija os serviços antes de voltar a verificar a disponibilidade.'
				severity='warning'
				showCloseButton={false}
				closeOnBackdropClick={false}
				onRequestClose={() => setIsInvalidDatesDialogOpen(false)}
				actions={[
					{
						label: 'OK',
					},
				]}
			/>
			<CustomAlertDialog
				open={saveAvailabilityError}
				title='Erro ao guardar disponibilidade'
				description='Não foi possível recalcular a disponibilidade com a pausa selecionada. Tente novamente.'
				severity='error'
				showCloseButton={false}
				closeOnBackdropClick={false}
				onRequestClose={() => setSaveAvailabilityError(false)}
				actions={[
					{
						label: 'OK',
					},
				]}
			/>
		</>
	)
}

export default WorkRegimeAndAgent
